/**
 * Search params contract for /transactions. The list filters live
 * in the URL (TanStack Router) so they are shareable and survive
 * navigation; `txId` is a one-shot command: "open this tx in the editor".
 */

export interface TxListSearch {
  /** Text against note and payee. */
  text?: string;
  categoryId?: string;
  walletId?: string;
  /** Partial match against payee. */
  payee?: string;
  /** Inclusive date range, YYYY-MM-DD (replaces accounting month). */
  from?: string;
  to?: string;
  /** Amount range in MAJOR units of the base currency. */
  min?: number;
  max?: number;
  /**
   * Opens the edit dialog for this tx. Persists while the dialog is
   * open (#15b): the URL reflects the editor and is copyable/re-openable via back.
   */
  txId?: string;
  /**
   * Creation dialog open. `1` = new transaction (default type:
   * expense). The specific type is in turn addressable as its own step of the creation
   * flow: `?new=expense|income|transfer` opens the form directly in
   * that type (e.g. "new transfer" has its own shareable URL).
   */
  new?: 1 | "expense" | "income" | "transfer";
  /** Navigated accounting month (`YYYY-MM`); absent = current month (default omitted). */
  month?: string;
  /** Reconciliation mode on: `?reconcile=1`. */
  reconcile?: 1;
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function asMonth(value: unknown): string | undefined {
  return typeof value === "string" && MONTH_RE.test(value) ? value : undefined;
}

function asFlag(value: unknown): 1 | undefined {
  return value === true || value === "1" || value === 1 ? 1 : undefined;
}

/**
 * Normalizes `?new`: a tx type (`expense|income|transfer`) opens creation
 * directly in that step; any other truthy value ("1"/1/true) opens
 * creation with default type (`1`). Absent/falsy → closed.
 */
function asNewFlag(value: unknown): 1 | "expense" | "income" | "transfer" | undefined {
  if (value === "expense" || value === "income" || value === "transfer") {
    return value;
  }
  return asFlag(value);
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

/** Route validateSearch: discards unknown keys and invalid types. */
export function sanitizeTxListSearch(search: Record<string, unknown>): TxListSearch {
  return {
    text: asString(search.text),
    categoryId: asString(search.categoryId),
    walletId: asString(search.walletId),
    payee: asString(search.payee),
    from: asString(search.from),
    to: asString(search.to),
    min: asNumber(search.min),
    max: asNumber(search.max),
    txId: asString(search.txId),
    new: asString(search.txId) ? undefined : asNewFlag(search.new),
    month: asMonth(search.month),
    reconcile: asFlag(search.reconcile),
  };
}

/** Number of active filters (for the filter button badge). */
export function countActiveTxFilters(search: TxListSearch): number {
  let n = 0;
  if (search.text) {
    n += 1;
  }
  if (search.categoryId) {
    n += 1;
  }
  if (search.walletId) {
    n += 1;
  }
  if (search.payee) {
    n += 1;
  }
  if (search.from || search.to) {
    n += 1;
  }
  if (search.min !== undefined || search.max !== undefined) {
    n += 1;
  }
  return n;
}
